"use client";

import {
  FEATURE_SLIDE_THEMES,
  type FeatureSlideData
} from "@gettactic/defines/src/feature-announcements";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { ConfettiExplosion } from "./ConfettiExplosion";
import { useFeatureAction } from "./FeatureActionContext";
import { SlideCard } from "./SlideCard";
import type { FeatureAnnouncementProps } from "./types";

export const FeatureAnnouncementModal: React.FC<FeatureAnnouncementProps> = ({
  isOpen: controlledIsOpen,
  slide: singleSlide,
  slides: multipleSlides,
  onDismiss,
  onClose,
  showConfetti = true
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [nextSlide, setNextSlide] = useState<number | null>(null);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right"
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const [showConfettiExplosion, setShowConfettiExplosion] = useState(false);

  // Store slides in a ref to preserve them during close animation
  const slidesRef = useRef<FeatureSlideData[]>([]);
  const isSingleSlideModeRef = useRef(false);

  const { executeAction } = useFeatureAction();

  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  // Determine slides to show - only update when modal is open
  const slides = useMemo(() => {
    if (singleSlide) {
      return [singleSlide];
    }
    if (multipleSlides && multipleSlides.length > 0) {
      return multipleSlides;
    }
    return [];
  }, [singleSlide, multipleSlides]);

  // Update refs only when modal opens with new content
  useEffect(() => {
    if (isOpen && (singleSlide || multipleSlides)) {
      slidesRef.current = slides;
      isSingleSlideModeRef.current = slides.length === 1;
      // Reset to first slide when modal opens to prevent index out of bounds
      setCurrentSlide(0);
      setNextSlide(null);
      setIsAnimating(false);
    }
  }, [isOpen, singleSlide, multipleSlides, slides]);

  // Trigger confetti explosion when modal opens (with slight delay for modal to appear first)
  useEffect(() => {
    if (isOpen && showConfetti) {
      const timer = setTimeout(() => {
        setShowConfettiExplosion(true);
      }, 150);
      return () => clearTimeout(timer);
    } else {
      setShowConfettiExplosion(false);
    }

    return undefined;
  }, [isOpen, showConfetti]);

  // Use ref values to prevent glitch during close animation
  const displaySlides = isOpen ? slides : slidesRef.current;
  const isSingleSlideMode = isOpen
    ? slides.length === 1
    : isSingleSlideModeRef.current;

  // Close without marking as read (X button, ESC, outside click)
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      setInternalIsOpen(false);
    }
  }, [onClose]);

  // Dismiss and mark as read (Try button, Maybe Later button)
  // The parent controls whether modal stays open (e.g., to show next feature)
  const handleDismiss = useCallback(
    (featureId?: string, action?: "try" | "maybe-later") => {
      if (onDismiss) {
        onDismiss(featureId, action);
      } else if (onClose) {
        onClose();
      } else {
        setInternalIsOpen(false);
      }
    },
    [onDismiss, onClose]
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Dialog closed via ESC or outside click - don't mark as read
      handleClose();
    }
  };

  const handleTryAction = useCallback(
    (slideIndex: number) => {
      const slide = displaySlides[slideIndex];
      executeAction(slide.tryAction);
      handleDismiss(slide.id, "try");
    },
    [displaySlides, executeAction, handleDismiss]
  );

  const handleMaybeLater = useCallback(
    (slideIndex: number) => {
      const slide = displaySlides[slideIndex];
      handleDismiss(slide.id, "maybe-later");
    },
    [displaySlides, handleDismiss]
  );

  const goToSlide = (direction: "prev" | "next") => {
    if (isAnimating || isSingleSlideMode) return;

    const newSlide =
      direction === "next"
        ? (currentSlide + 1) % displaySlides.length
        : (currentSlide - 1 + displaySlides.length) % displaySlides.length;

    setSlideDirection(direction === "next" ? "right" : "left");
    setNextSlide(newSlide);
    setIsAnimating(true);

    setTimeout(() => {
      setCurrentSlide(newSlide);
    }, 480);

    setTimeout(() => {
      setNextSlide(null);
      setIsAnimating(false);
    }, 500);
  };

  const goToSlideByIndex = (index: number) => {
    if (index === currentSlide || isAnimating || isSingleSlideMode) return;

    setSlideDirection(index > currentSlide ? "right" : "left");
    setNextSlide(index);
    setIsAnimating(true);

    setTimeout(() => {
      setCurrentSlide(index);
    }, 480);

    setTimeout(() => {
      setNextSlide(null);
      setIsAnimating(false);
    }, 500);
  };

  const handleConfettiComplete = useCallback(() => {
    setShowConfettiExplosion(false);
  }, []);

  if (displaySlides.length === 0) {
    return null;
  }

  const currentSlideData = displaySlides[currentSlide];
  const currentTheme = FEATURE_SLIDE_THEMES[currentSlideData.themeName];

  return (
    <>
      {/* Confetti explosion rendered via portal to appear behind modal */}
      {showConfettiExplosion && (
        <ConfettiExplosion
          intensity={currentSlideData?.confettiIntensity}
          onComplete={handleConfettiComplete}
        />
      )}

      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[10000] overflow-hidden bg-white/60 data-[state=closed]:animate-out data-[state=open]:animate-in" />

          <Dialog.Content className="-translate-x-1/2 -translate-y-1/2 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-1/2 left-1/2 z-[10001] w-full max-w-lg px-8 data-[state=closed]:animate-out data-[state=open]:animate-in">
            <div className="relative" style={{ perspective: "1200px" }}>
              {/* Navigation Arrows - only show in multi-slide mode */}
              {!isSingleSlideMode && (
                <>
                  <button
                    type="button"
                    onClick={() => goToSlide("prev")}
                    className="-left-6 -translate-y-1/2 absolute top-1/2 z-30 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-neutral-200 bg-white text-[#0F2449] shadow-xl backdrop-blur-md transition-all hover:scale-110 hover:bg-neutral-50"
                    aria-label="Previous feature"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    onClick={() => goToSlide("next")}
                    className="-right-6 -translate-y-1/2 absolute top-1/2 z-30 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-neutral-200 bg-white text-[#0F2449] shadow-xl backdrop-blur-md transition-all hover:scale-110 hover:bg-neutral-50"
                    aria-label="Next feature"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}

              {/* Cards Container */}
              <div className="relative">
                {/* Current Card (exits) */}
                <SlideCard
                  slide={currentSlideData}
                  theme={currentTheme}
                  currentSlideIndex={currentSlide}
                  totalSlides={isSingleSlideMode ? 1 : displaySlides.length}
                  onClose={handleClose}
                  onTryAction={() => handleTryAction(currentSlide)}
                  onMaybeLater={() => handleMaybeLater(currentSlide)}
                  onIndicatorClick={goToSlideByIndex}
                  animationState={isAnimating ? "exiting" : "active"}
                  direction={slideDirection}
                />

                {/* Next Card (enters) - only in multi-slide mode */}
                {!isSingleSlideMode && nextSlide !== null && (
                  <SlideCard
                    slide={displaySlides[nextSlide]}
                    theme={
                      FEATURE_SLIDE_THEMES[displaySlides[nextSlide].themeName]
                    }
                    currentSlideIndex={nextSlide}
                    totalSlides={displaySlides.length}
                    onClose={handleClose}
                    onTryAction={() => handleTryAction(nextSlide)}
                    onMaybeLater={() => handleMaybeLater(nextSlide)}
                    onIndicatorClick={goToSlideByIndex}
                    animationState="entering"
                    direction={slideDirection}
                  />
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};
